import { annualOf, getFyFromDate } from '../budget/variance';
import type {
  BudgetDocument, BudgetHead, BudgetItem, BudgetTransaction, Expense, Numeric, VarianceRow, VarianceStatus
} from '../../types';

// Ties an expense to the budget line it is spent against.
//
// The Annual Budget is a tree — financial year, then heads, then items:
//
//   2026-2027
//     Pastoral Ministry
//       a. Pastor Salary    25,000 monthly
//       b. Annual Retreat   40,000 one-time
//
// The expense form now picks from that tree instead of a free list: Category is
// a head, Expense Name is one of its items. Both are stored on the expense as
// plain text, which is also how the variance report already attributes spend, so
// no new link column is needed.
//
// WHAT IS SPENT IS COUNTED, NEVER STORED. The remaining balance is worked out by
// adding up the live expenses on a line. Incrementing a saved total would drift
// the moment an expense was voided or its amount corrected — this way voiding an
// expense simply gives the money back, with nothing to reconcile.

const num = (v?: Numeric | null): number => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const clean = (v?: string | null): string => String(v ?? '').trim();

/** Case-insensitive, so "Pastor salary" still finds "Pastor Salary". */
const key = (v?: string | null): string => clean(v).toLowerCase();

/** The heads of one financial year, in the order the budget lists them. */
export const headsIn = (document?: BudgetDocument | null, fy?: string): BudgetHead[] => {
  const heads = (document && fy ? document[fy] : undefined) || [];
  return [...heads].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
};

export const headNamesIn = (document?: BudgetDocument | null, fy?: string): string[] =>
  headsIn(document, fy).map((h) => clean(h.headName)).filter(Boolean);

/** The items under one head. Empty when the head has none, or is unknown. */
export const itemsUnder = (
  document?: BudgetDocument | null, fy?: string, headName?: string
): BudgetItem[] => {
  const head = headsIn(document, fy).find((h) => key(h.headName) === key(headName));
  return [...(head?.items || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
};

export const itemNamesUnder = (
  document?: BudgetDocument | null, fy?: string, headName?: string
): string[] => itemsUnder(document, fy, headName).map((i) => clean(i.name)).filter(Boolean);

/** The financial year an expense belongs to, taken from its date. */
export const fyOfExpense = (date?: string): string => getFyFromDate(date);

/** April to March. Used to fetch only the expenses that count against a year. */
export const fyRange = (fy: string): { from: string; to: string } => {
  const start = parseInt(String(fy).split('-')[0], 10);
  return Number.isFinite(start)
    ? { from: `${start}-04-01`, to: `${start + 1}-03-31` }
    : { from: '', to: '' };
};

/**
 * What each budget line has been spent, keyed by "head||item".
 *
 * Voided expenses are left out, so voiding one returns its money to the budget.
 * The row currently being edited is excluded too — otherwise raising an amount
 * from 5,000 to 6,000 would count the original 5,000 twice and report the line
 * as far more spent than it is.
 */
export const spendByLine = (
  expenses: Expense[] = [],
  excludeId?: number | string | null
): Map<string, number> => {
  const out = new Map<string, number>();
  expenses.forEach((e) => {
    if (e.status === 'VOID') return;
    if (excludeId !== undefined && excludeId !== null && e.id === excludeId) return;
    const k = `${key(e.category)}||${key(e.expenseName)}`;
    out.set(k, (out.get(k) || 0) + num(e.amount));
  });
  return out;
};

/**
 * The individual expenses behind a line's Actual figure, newest first.
 *
 * The total is a sum, and a sum on its own cannot be checked. Being able to open
 * it and see the entries that make it up is what turns "spent 1,09,999.58" into
 * something a treasurer can verify against the receipts.
 */
export const expensesForLine = (
  expenses: Expense[] = [],
  headName?: string,
  itemName?: string
): Expense[] =>
  expenses
    .filter((e) => e.status !== 'VOID')
    .filter((e) => key(e.category) === key(headName) && key(e.expenseName) === key(itemName))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

export interface LineBudget {
  /** The line was found in the budget. */
  known: boolean;
  /** Annual allowance: a monthly item counts twelve times. */
  budget: number;
  /** Already spent on this line, this year, excluding the row being edited. */
  spent: number;
  /** What is left before this expense. Never below zero. */
  remaining: number;
  /** True once `amount` takes the line past its allowance. */
  over: boolean;
  /** How far past, or 0. */
  overBy: number;
}

/**
 * The state of one budget line, with a proposed amount applied.
 *
 * A line with no budget still reports honestly — `known: false`, everything
 * zero — so the form can say "this line is not budgeted" rather than implying
 * an allowance of nothing.
 */
export const lineBudget = (
  document: BudgetDocument | null | undefined,
  fy: string,
  headName: string,
  itemName: string,
  spend: Map<string, number>,
  amount: Numeric = 0
): LineBudget => {
  const item = itemsUnder(document, fy, headName)
    .find((i) => key(i.name) === key(itemName));

  const spent = spend.get(`${key(headName)}||${key(itemName)}`) || 0;
  if (!item) {
    return { known: false, budget: 0, spent, remaining: 0, over: false, overBy: 0 };
  }

  const budget = annualOf(item);
  const remaining = Math.max(0, budget - spent);
  const after = spent + num(amount);
  const overBy = Math.max(0, after - budget);

  return { known: true, budget, spent, remaining, over: overBy > 0, overBy };
};

// --- split payments -------------------------------------------------------

export const SPLIT = 'Split';
export const PAYMENT_MODES = ['Cash', 'Bank', 'UPI', 'Cheque', SPLIT] as const;
/** The modes a split can be divided between — Split itself is not one of them. */
export const SPLIT_MODES = ['Cash', 'Bank', 'UPI', 'Cheque'] as const;

export interface SplitPart {
  mode: string;
  amount: Numeric;
}

export const splitTotal = (parts: SplitPart[] = []): number =>
  parts.reduce((sum, p) => sum + num(p.amount), 0);

/**
 * Why a split is not acceptable, or null when it is.
 *
 * The parts have to add up to the total exactly. A split that does not balance
 * is worse than no split at all: the expense would claim one figure while its
 * payment record showed another, and neither would be obviously wrong.
 *
 * Compared to the paisa to avoid a floating-point remainder rejecting a split
 * that is right to the rupee.
 */
export const validateSplit = (parts: SplitPart[] = [], total: Numeric = 0): string | null => {
  const filled = parts.filter((p) => clean(p.mode) && num(p.amount) > 0);
  if (filled.length < 2) return 'A split payment needs at least two parts.';
  if (filled.some((p) => num(p.amount) < 0)) return 'A part cannot be negative.';

  const modes = filled.map((p) => key(p.mode));
  if (new Set(modes).size !== modes.length) return 'Each part must use a different payment mode.';

  const sum = Math.round(splitTotal(filled) * 100);
  const target = Math.round(num(total) * 100);
  if (sum !== target) {
    const diff = (target - sum) / 100;
    return diff > 0
      ? `The parts are short by ₹${diff.toFixed(2)} of the total.`
      : `The parts exceed the total by ₹${Math.abs(diff).toFixed(2)}.`;
  }
  return null;
};

/** Only the parts worth storing, tidied. */
export const cleanSplit = (parts: SplitPart[] = []): SplitPart[] =>
  parts
    .filter((p) => clean(p.mode) && num(p.amount) > 0)
    .map((p) => ({ mode: clean(p.mode), amount: num(p.amount) }));

/** Reads back what was stored. Never throws on a malformed value. */
export const parseSplit = (stored?: string | null): SplitPart[] => {
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed)
      ? parsed.filter((p) => p && typeof p === 'object').map((p) => ({
        mode: clean((p as SplitPart).mode),
        amount: num((p as SplitPart).amount)
      }))
      : [];
  } catch {
    return [];
  }
};

/** "Cash ₹5,000 + Bank ₹15,000", for the list and the printed voucher. */
export const describeSplit = (parts: SplitPart[] = []): string =>
  cleanSplit(parts)
    .map((p) => `${p.mode} ₹${num(p.amount).toLocaleString('en-IN')}`)
    .join(' + ');

// --- Budget vs Actual -----------------------------------------------------

/**
 * Under- and over-spend read differently, so the bands are not symmetrical:
 * comfortably under an expense line is healthy, past it is not.
 *
 * A line with no allowance is judged on whether anything moved at all —
 * otherwise a zero budget divides into nothing and every unbudgeted rupee
 * would be reported as perfectly on target.
 */
const statusOf = (budget: number, actual: number): VarianceStatus => {
  if (budget <= 0) return actual > 0 ? 'bad' : 'good';
  const pct = (actual / budget) * 100;
  return pct <= 85 ? 'good' : pct <= 100 ? 'warn' : 'bad';
};

/** What kind of row a Budget vs Actual line is. */
export type VarianceRowKind =
  /** A sub-item of the budget document. */
  | 'line'
  /** Expenses booked under a head with a line name the budget does not have. */
  | 'other'
  /** Budget Adjustments for a head: Income raises the allotment, Expense counts as spend. */
  | 'adjustment'
  /** Spend under a category that is not a head of this year's budget at all. */
  | 'unbudgeted';

/** Labels for the rows that gather what no budget line names. */
export const OTHER_LINE = 'Other expenses under this head';
export const ADJUSTMENT_LINE = 'Budget adjustments';
export const UNBUDGETED_HEAD = 'Not in budget';

/** One row of Budget vs Actual, carrying which budget line it describes. */
export interface BudgetLineVariance extends VarianceRow {
  /** The budget item, e.g. "Pastor Salary". `head` holds "Pastoral Ministry". */
  item: string;
  kind: VarianceRowKind;
  /**
   * What should have been spent by today if the allowance is used evenly over
   * the year — null for a year that has not started or a line with no budget.
   * Actual above this means the line is running ahead of its budget.
   */
  expected: number | null;
}

/**
 * How much of a financial year has gone by on `today`, 0..1, to the day.
 * A year not yet begun is 0; a finished year is 1.
 */
export const fyElapsedFraction = (fy: string, today: Date = new Date()): number => {
  const start = parseInt(String(fy).split('-')[0], 10);
  if (!Number.isFinite(start)) return 0;
  const from = new Date(start, 3, 1);
  const to = new Date(start + 1, 3, 1);
  if (today < from) return 0;
  if (today >= to) return 1;
  return (today.getTime() - from.getTime()) / (to.getTime() - from.getTime());
};

/** Spend under each head, whatever the line name — keyed by the lower-cased head. */
export const spendByHead = (expenses: Expense[] = []): Map<string, number> => {
  const out = new Map<string, number>();
  expenses.forEach((e) => {
    if (e.status === 'VOID') return;
    const k = key(e.category);
    out.set(k, (out.get(k) || 0) + num(e.amount));
  });
  return out;
};

/**
 * An adjustment counts only once approved. Rows saved before approval existed
 * carry no status and are treated as approved, so an old ledger does not
 * suddenly lose its adjustments.
 */
export const adjustmentCounts = (a: BudgetTransaction): boolean =>
  a.status !== 'VOID' && (a.approvalStatus == null || a.approvalStatus === 'Approved');

/**
 * The financial year's approved adjustments, per head: Income entries add to
 * the allotment (a supplementary budget); Expense entries are spend. Voided and
 * unapproved entries are ignored. Keyed by the lower-cased head.
 */
export const adjustmentsByHead = (
  adjustments: BudgetTransaction[] = [],
  fy: string
): Map<string, { budget: number; actual: number }> => {
  const out = new Map<string, { budget: number; actual: number }>();
  adjustments.forEach((a) => {
    if (!adjustmentCounts(a) || clean(a.fy) !== fy) return;
    const k = key(a.head);
    const entry = out.get(k) || { budget: 0, actual: 0 };
    if (String(a.type).toLowerCase() === 'income') entry.budget += num(a.amount);
    else entry.actual += num(a.amount);
    out.set(k, entry);
  });
  return out;
};

const row = (
  fy: string, head: string, item: string, kind: VarianceRowKind,
  budget: number, actual: number, elapsed: number
): BudgetLineVariance => ({
  fy,
  head,
  item,
  kind,
  dept: head,
  type: 'Expense' as const,
  budget,
  actual,
  // Positive is money still available; negative means the line overran.
  varianceAmt: budget - actual,
  variancePct: budget > 0 ? (actual / budget) * 100 : 0,
  status: statusOf(budget, actual),
  expected: elapsed > 0 && budget > 0 ? budget * elapsed : null
});

/**
 * Budget vs Actual, built from the Annual Budget Document.
 *
 * This is the report that was previously assembled from the flat Budget
 * Preparation rows, which never met the expenses: budgets were bucketed by their
 * own department while every expense was filed under "General Fund", so the two
 * could not land in the same bucket and Actual always read as zero.
 *
 * Now both sides come from the same place. An expense stores the head and item
 * it was booked against, so a line and its spending share a name by construction
 * rather than by luck.
 *
 * NOTHING SPENT IS LEFT OUT. An expense under a head whose line name the budget
 * does not have lands on an "Other expenses under this head" row; one under a
 * category that is not a head at all lands under "Not in budget". So the head
 * subtotals and the grand total always equal the expense register — the same
 * figures Financial Analytics shows — rather than only the part that happened
 * to be named exactly like a budget line.
 *
 * Budget Adjustments for a head appear on their own row: Income adjustments
 * raise the head's allotment, Expense adjustments count as spend.
 */
export const buildDocumentVariance = (
  document: BudgetDocument | null | undefined,
  fy: string,
  expenses: Expense[] = [],
  adjustments: BudgetTransaction[] = [],
  today: Date = new Date()
): BudgetLineVariance[] => {
  const fyExpenses = expenses.filter((e) => fyOfExpense(e.date) === fy);
  const spend = spendByLine(fyExpenses);
  const headSpend = spendByHead(fyExpenses);
  const adjusted = adjustmentsByHead(adjustments, fy);
  const elapsed = fyElapsedFraction(fy, today);

  const rows: BudgetLineVariance[] = [];
  const knownHeads = new Set<string>();

  headsIn(document, fy).forEach((head) => {
    const headName = clean(head.headName);
    const headKey = key(headName);
    knownHeads.add(headKey);

    let matched = 0;
    (head.items || []).forEach((item) => {
      const actual = spend.get(`${headKey}||${key(item.name)}`) || 0;
      matched += actual;
      rows.push(row(fy, headName, clean(item.name), 'line', annualOf(item), actual, elapsed));
    });

    // Whatever was booked under the head but not against a named line.
    const other = (headSpend.get(headKey) || 0) - matched;
    if (other > 0.005) rows.push(row(fy, headName, OTHER_LINE, 'other', 0, other, elapsed));

    const adj = adjusted.get(headKey);
    if (adj && (adj.budget || adj.actual)) {
      rows.push(row(fy, headName, ADJUSTMENT_LINE, 'adjustment', adj.budget, adj.actual, elapsed));
    }
  });

  // Categories that are not heads of this year's budget: still money spent.
  const unbudgeted = new Map<string, { budget: number; actual: number; label: string }>();
  fyExpenses.forEach((e) => {
    if (e.status === 'VOID') return;
    const k = key(e.category);
    if (knownHeads.has(k)) return;
    const entry = unbudgeted.get(k) || { budget: 0, actual: 0, label: clean(e.category) || 'Uncategorised' };
    entry.actual += num(e.amount);
    unbudgeted.set(k, entry);
  });
  adjusted.forEach((adj, k) => {
    if (knownHeads.has(k)) return;
    const source = adjustments.find((a) => key(a.head) === k);
    const entry = unbudgeted.get(k) || { budget: 0, actual: 0, label: clean(source?.head) || 'Uncategorised' };
    entry.budget += adj.budget;
    entry.actual += adj.actual;
    unbudgeted.set(k, entry);
  });
  Array.from(unbudgeted.values())
    .sort((a, b) => b.actual - a.actual)
    .forEach((entry) => rows.push(row(fy, UNBUDGETED_HEAD, entry.label, 'unbudgeted', entry.budget, entry.actual, elapsed)));

  return rows;
};

/** The totals under the Budget vs Actual table. */
export const varianceTotals = (rows: BudgetLineVariance[] = []) => {
  const budget = rows.reduce((sum, r) => sum + r.budget, 0);
  const actual = rows.reduce((sum, r) => sum + r.actual, 0);
  const expected = rows.reduce((sum, r) => sum + (r.expected || 0), 0);
  const unbudgeted = rows
    .filter((r) => r.kind === 'unbudgeted' || r.kind === 'other')
    .reduce((sum, r) => sum + r.actual, 0);
  return {
    budget,
    actual,
    expected,
    unbudgeted,
    remaining: budget - actual,
    overspent: rows.filter((r) => r.kind === 'line' && r.actual > r.budget).length
  };
};

// --- Monthly burn ----------------------------------------------------------

/** Apr..Mar, the order a financial year runs. */
export const FY_MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

export interface HeadBurnRow {
  head: string;
  /** Twelve figures, April first. */
  months: number[];
  total: number;
  allotted: number;
  /** allotted / 12 — the even monthly pace, for reading the row against. */
  monthly: number;
}

/**
 * Spend per head per month of the financial year, so an overrun shows WHEN it
 * happened, not just that it did. Every head of the document is listed (zero
 * rows included) and unbudgeted categories are gathered on one final row.
 */
export const monthlyBurn = (
  document: BudgetDocument | null | undefined,
  fy: string,
  expenses: Expense[] = [],
  adjustments: BudgetTransaction[] = []
): HeadBurnRow[] => {
  const known = new Map<string, HeadBurnRow>();
  headsIn(document, fy).forEach((h) => {
    const allotted = (h.items || []).reduce((sum, i) => sum + annualOf(i), 0);
    known.set(key(h.headName), {
      head: clean(h.headName), months: Array(12).fill(0), total: 0, allotted, monthly: allotted / 12
    });
  });
  const other: HeadBurnRow = { head: UNBUDGETED_HEAD, months: Array(12).fill(0), total: 0, allotted: 0, monthly: 0 };

  const monthIndex = (date?: string): number => {
    const d = new Date(String(date || ''));
    if (isNaN(d.getTime())) return -1;
    return (d.getMonth() + 9) % 12; // April -> 0
  };
  const book = (headKey: string, date: string | undefined, amount: number) => {
    const i = monthIndex(date);
    if (i < 0) return;
    const target = known.get(headKey) || other;
    target.months[i] += amount;
    target.total += amount;
  };

  expenses
    .filter((e) => e.status !== 'VOID' && fyOfExpense(e.date) === fy)
    .forEach((e) => book(key(e.category), e.date, num(e.amount)));
  adjustments
    .filter((a) => adjustmentCounts(a) && clean(a.fy) === fy && String(a.type).toLowerCase() !== 'income')
    .forEach((a) => book(key(a.head), a.date, num(a.amount)));

  const rows = Array.from(known.values());
  if (other.total > 0) rows.push(other);
  return rows;
};
